type ObservableValueListener<T, Ctx> = (value: T, ctx?: Ctx) => void

type Token = unknown

/**
 * Обозреваемое значение. При изменении оповещает список слушателей.
 */
export class ObservableValue<T, Ctx = unknown> {
  private listeners = new Map<Token, ObservableValueListener<T, Ctx>>()

  constructor(private internalValue: T) {}

  get value(): T {
    return this.internalValue
  }

  setValue(newValue: T, ctx?: Ctx) {
    if (this.internalValue === newValue) {
      return
    }
    this.internalValue = newValue
    this.listeners.forEach(listener => {
      listener(newValue, ctx)
    })
  }

  subscribe(listener: ObservableValueListener<T, Ctx>): Token {
    this.listeners.set(listener, listener)
    return listener
  }

  unsubscribe(token: Token): void {
    this.listeners.delete(token)
  }
}

/**
 * Создание обозреваемого значения.
 *
 * @param initialValue первоначальное значение
 */
export function createObservableValue<T, Ctx = unknown>(initialValue: T): ObservableValue<T, Ctx> {
  return new ObservableValue<T, Ctx>(initialValue)
}

/**
 * Создание опционального обозреваемого значения.
 *
 * @param initialValue первоначальное значение
 */
export function createOptionalObservableValue<T, Ctx = unknown>(initialValue?: T): ObservableValue<T | undefined, Ctx> {
  if (initialValue !== undefined) return new ObservableValue<T | undefined, Ctx>(initialValue)
  else return new ObservableValue<T | undefined, Ctx>(undefined)
}

type ObservableActionListener<T, Ctx> = (action: T, ctx?: Ctx) => void

export class ObservableAction<T, Ctx = unknown> {
  private listeners = new Map<Token, ObservableActionListener<T, Ctx>>()

  emit(action: T, ctx?: Ctx) {
    this.listeners.forEach(listener => {
      listener(action, ctx)
    })
  }

  subscribe(listener: ObservableActionListener<T, Ctx>): Token {
    this.listeners.set(listener, listener)
    return listener
  }
  unsubscribe(token: Token): void {
    this.listeners.delete(token)
  }
}

export function createObservableAction<T, Ctx = unknown>(): ObservableAction<T, Ctx> {
  return new ObservableAction<T, Ctx>()
}
